import { useMemo, useState } from 'react';
import { Glasses, ExternalLink, FileText, Check, ChevronDown, ChevronRight } from 'lucide-react';

export interface UnverifiedLink {
  targetUrl: string;
  sourceUrl: string;
  statusCode?: number;
}

interface UnverifiedLinksSectionProps {
  links: UnverifiedLink[];
  /** What the check was looking at, used in the intro line ("outbound links") */
  subject: string;
  /** Target URLs already checked by hand in this scan */
  reviewedLinks?: string[];
  onToggleReviewed?: (targetUrls: string[], reviewed: boolean) => void;
}

function truncateUrl(url: string, maxLength = 70): string {
  return url.length <= maxLength ? url : `${url.slice(0, maxLength)}...`;
}

function reasonLabel(statusCode?: number): string {
  if (!statusCode) return 'No answer within 15s, may just be slow';
  if (statusCode === 403) return 'HTTP 403 refused (CAPTCHA or bot filter)';
  if (statusCode === 429) return 'HTTP 429 rate limited';
  if (statusCode === 999) return 'HTTP 999 refused (LinkedIn blocks automated visits)';
  if (statusCode === 401) return 'HTTP 401 sign-in required';
  return `HTTP ${statusCode}`;
}

function domainOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

interface LinkRow {
  link: UnverifiedLink;
  pageCount: number;
}

interface DomainGroup {
  domain: string;
  rows: LinkRow[];
  /** One reason if the whole domain answered the same way, otherwise null */
  sharedReason: string | null;
}

/**
 * Links the scanner could not settle on: an anti-bot 403, a rate limit, a 5xx or no
 * answer within the probe timeout, on every retry. Reporting them as broken blamed
 * live pages behind a CAPTCHA and made the count flap between runs; dropping them
 * silently hid links a human should eyeball. So they are listed here instead -
 * unscored, out of every count, deduped by target URL to match the Manual review
 * total in the header.
 *
 * Grouped by target domain, because that is the unit of the actual decision: a site
 * either refuses automated visits or it does not. Seventeen cookie-name links on one
 * page behind one bot filter are one question, not seventeen - so a domain can be
 * ticked off in one go, and opened when the individual URLs matter.
 *
 * A tick belongs to this scan only: the next scan probes every link again from
 * scratch, so it starts clean rather than inheriting an earlier judgement.
 *
 * Shown on the Broken Links tab; the Toxic Links tab only shows it when that scan
 * ran without the broken-link check, since otherwise both tabs list the same links.
 */
export function UnverifiedLinksSection({
  links,
  subject,
  reviewedLinks = [],
  onToggleReviewed,
}: UnverifiedLinksSectionProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const reviewed = useMemo(() => new Set(reviewedLinks), [reviewedLinks]);

  const { groups, total } = useMemo(() => {
    const byTarget = new Map<string, LinkRow>();
    for (const link of links) {
      const existing = byTarget.get(link.targetUrl);
      if (existing) existing.pageCount++;
      else byTarget.set(link.targetUrl, { link, pageCount: 1 });
    }

    const byDomain = new Map<string, LinkRow[]>();
    for (const row of byTarget.values()) {
      const domain = domainOf(row.link.targetUrl);
      const rows = byDomain.get(domain);
      if (rows) rows.push(row);
      else byDomain.set(domain, [row]);
    }

    const grouped: DomainGroup[] = [...byDomain.entries()]
      .map(([domain, rows]) => {
        const reasons = new Set(rows.map((r) => reasonLabel(r.link.statusCode)));
        return { domain, rows, sharedReason: reasons.size === 1 ? [...reasons][0] : null };
      })
      .sort((a, b) => b.rows.length - a.rows.length);

    return { groups: grouped, total: byTarget.size };
  }, [links]);

  if (total === 0) return null;

  const remaining =
    total - [...reviewed].filter((url) => links.some((l) => l.targetUrl === url)).length;

  const toggleExpanded = (domain: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });

  return (
    <div className="card">
      <h3 className="text-heading-3 font-semibold mb-2 flex items-center gap-2">
        <Glasses className="text-gray-medium" size={24} />
        Could not be verified ({remaining}
        {remaining !== total && ` of ${total}`})
      </h3>
      <p className="text-body-sm text-gray-medium mb-4">
        These {subject} refused or never answered the scanner. Usually that means a live page behind
        a CAPTCHA or bot filter. They are not counted as broken and do not affect the score. Open
        one link per domain to see whether the site is up, then tick the domain off. A tick is
        remembered for 90 days, so later scans stop asking. If a link does go missing later, it
        comes back as a broken link.
      </p>

      <ul className="space-y-2">
        {groups.map((group) => {
          const checkedCount = group.rows.filter(({ link }) => reviewed.has(link.targetUrl)).length;
          const allChecked = checkedCount === group.rows.length;
          const someChecked = checkedCount > 0 && !allChecked;
          const isOpen = expanded.has(group.domain);
          const urls = group.rows.map((r) => r.link.targetUrl);

          return (
            <li
              key={group.domain}
              className={`rounded-lg border ${
                allChecked
                  ? 'bg-status-success/5 border-status-success/30'
                  : 'bg-gray-light/30 border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3 p-3">
                {onToggleReviewed && (
                  <label className="flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={(e) => onToggleReviewed(urls, e.target.checked)}
                      className="w-4 h-4 accent-status-success cursor-pointer"
                    />
                    <span className="sr-only">Mark every link to {group.domain} as checked</span>
                  </label>
                )}

                <button
                  onClick={() => toggleExpanded(group.domain)}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 text-left flex-1 min-w-0"
                  aria-expanded={isOpen}
                >
                  {isOpen ? (
                    <ChevronDown size={16} className="text-gray-medium flex-shrink-0" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-medium flex-shrink-0" />
                  )}
                  <span
                    className={`text-body-sm font-medium ${
                      allChecked ? 'text-gray-medium line-through' : 'text-dark-text'
                    }`}
                  >
                    {group.domain}
                  </span>
                  <span className="text-caption text-gray-medium">
                    {group.rows.length} {group.rows.length === 1 ? 'link' : 'links'}
                  </span>
                  {allChecked ? (
                    <span className="text-caption px-2 py-0.5 rounded bg-status-success/10 text-status-success inline-flex items-center gap-1">
                      <Check size={12} />
                      Checked
                    </span>
                  ) : (
                    <span className="text-caption px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                      {group.sharedReason ?? 'Mixed responses'}
                    </span>
                  )}
                  {someChecked && (
                    <span className="text-caption text-gray-medium">
                      {checkedCount} of {group.rows.length} checked
                    </span>
                  )}
                </button>
              </div>

              {isOpen && (
                <ul className="border-t border-gray-200 divide-y divide-gray-200">
                  {group.rows.map(({ link, pageCount }) => {
                    const isReviewed = reviewed.has(link.targetUrl);
                    return (
                      <li
                        key={link.targetUrl}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 pl-10 pr-3"
                      >
                        {onToggleReviewed && (
                          <label className="flex items-center cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={isReviewed}
                              onChange={(e) => onToggleReviewed([link.targetUrl], e.target.checked)}
                              className="w-4 h-4 accent-status-success cursor-pointer"
                            />
                            <span className="sr-only">Mark {link.targetUrl} as checked</span>
                          </label>
                        )}

                        <a
                          href={link.targetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-body-sm hover:underline flex items-center gap-1 min-w-0 ${
                            isReviewed ? 'text-gray-medium line-through' : 'text-coral'
                          }`}
                          title={link.targetUrl}
                        >
                          <span className="truncate">{truncateUrl(link.targetUrl)}</span>
                          <ExternalLink size={14} className="flex-shrink-0" />
                        </a>

                        {!group.sharedReason && (
                          <span className="text-caption px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                            {reasonLabel(link.statusCode)}
                          </span>
                        )}

                        <span className="text-caption text-gray-medium flex items-center gap-1">
                          <FileText size={12} />
                          {pageCount === 1
                            ? truncateUrl(link.sourceUrl, 40)
                            : `Found on ${pageCount} pages`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
